import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { z } from "zod";

const blueskyMcpConnectSearchSchema = z.object({
	mcp_state: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/mcp/connect/bluesky")({
	component: BlueskyMcpConnectPage,
	validateSearch: (search) => blueskyMcpConnectSearchSchema.parse(search),
});

async function connectBluesky(body: {
	appPassword: string;
	identifier: string;
	mcpState: string;
}) {
	const response = await fetch("/api/social/bluesky/connection", {
		body: JSON.stringify(body),
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
		},
		method: "POST",
	});
	const payload = (await response.json().catch(() => null)) as {
		error?: string;
	} | null;
	if (!response.ok) {
		throw new Error(payload?.error || "Failed to connect Bluesky");
	}
}

function BlueskyMcpConnectPage() {
	const { mcp_state: mcpState } = Route.useSearch();
	const identifierId = useId();
	const appPasswordId = useId();
	const [identifier, setIdentifier] = useState("");
	const [appPassword, setAppPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isConnecting, setIsConnecting] = useState(false);
	const [isConnected, setIsConnected] = useState(false);

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		if (!mcpState) return;
		setError(null);
		setIsConnecting(true);
		try {
			await connectBluesky({ appPassword, identifier, mcpState });
			setAppPassword("");
			setIsConnected(true);
		} catch (nextError) {
			setError(
				nextError instanceof Error
					? nextError.message
					: "Failed to connect Bluesky",
			);
		} finally {
			setIsConnecting(false);
		}
	};

	return (
		<main className="min-h-screen bg-background px-4 py-8 text-foreground">
			<div className="mx-auto flex w-full max-w-md flex-col gap-6">
				<header className="space-y-2">
					<p className="text-sm font-medium text-sky-500">AgentReacher</p>
					<h1 className="text-2xl font-semibold tracking-normal">
						Connect Bluesky
					</h1>
					<p className="text-sm leading-6 text-muted-foreground">
						Bluesky uses app passwords for third-party tools. Create one in
						Bluesky, then paste it here. Do not use your main Bluesky password.
					</p>
				</header>

				{isConnected ? (
					<section className="rounded-md border border-border bg-card p-4">
						<div className="flex items-start gap-3">
							<CheckCircle2 className="mt-0.5 size-5 text-emerald-500" />
							<div className="space-y-1">
								<h2 className="text-sm font-medium">Bluesky connected</h2>
								<p className="text-sm text-muted-foreground">
									You can close this tab and return to ChatGPT.
								</p>
							</div>
						</div>
					</section>
				) : (
					<form
						onSubmit={handleSubmit}
						className="space-y-4 rounded-md border border-border bg-card p-4"
					>
						{!mcpState && (
							<p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
								This connection link is missing or expired. Start the Bluesky
								connection again from ChatGPT.
							</p>
						)}
						<div className="space-y-1.5">
							<label
								htmlFor={identifierId}
								className="text-xs font-medium text-foreground"
							>
								Bluesky handle
							</label>
							<input
								id={identifierId}
								type="text"
								value={identifier}
								onChange={(event) => setIdentifier(event.target.value)}
								placeholder="yourname.bsky.social"
								required
								className="flex h-10 w-full rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							/>
						</div>
						<div className="space-y-1.5">
							<label
								htmlFor={appPasswordId}
								className="text-xs font-medium text-foreground"
							>
								App password
							</label>
							<input
								id={appPasswordId}
								type="password"
								value={appPassword}
								onChange={(event) => setAppPassword(event.target.value)}
								placeholder="xxxx-xxxx-xxxx-xxxx"
								required
								className="flex h-10 w-full rounded-md border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							/>
							<a
								href="https://bsky.app/settings/app-passwords"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-1 text-xs font-medium text-sky-500 hover:underline"
							>
								Create an app password in Bluesky
								<ExternalLink className="size-3" />
							</a>
						</div>
						{error && (
							<p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
								{error}
							</p>
						)}
						<button
							type="submit"
							disabled={
								isConnecting ||
								!identifier.trim() ||
								!appPassword.trim() ||
								!mcpState
							}
							className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
						>
							{isConnecting && <Loader2 className="size-4 animate-spin" />}
							Connect Bluesky
						</button>
					</form>
				)}
			</div>
		</main>
	);
}
